import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  getIntegrationFields,
  updateIntegrationFields,
  UpdateIntegrationFieldsRequest,
  UpdateIntegrationFieldsResponse,
} from '../integrations2.0';

export const useIntegrationFields = (integrationKey: string, object: string, externalObjectKey: string, auth0: any) => {
  const queryClient = useQueryClient();

  const fieldsQuery = useQuery({
    queryKey: ['integrationFields', integrationKey],
    queryFn: () => getIntegrationFields(integrationKey, object, externalObjectKey, auth0),
    enabled: !!integrationKey && !!auth0,
  });

  const updateFieldMutation = useMutation({
    mutationFn: (fieldData: UpdateIntegrationFieldsRequest) =>
      updateIntegrationFields(
        integrationKey, 
        object, 
        externalObjectKey, 
        fieldData, 
        auth0
      ),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['integration', integrationKey] });
      queryClient.invalidateQueries({ queryKey: ['integrationFields', integrationKey] });
    },
  });

  const blacklistFieldMutation = useMutation({
    mutationFn: (blacklistField: { action: 'add' | 'remove'; field: string }) =>
      updateIntegrationFields(
        integrationKey,
        object,
        externalObjectKey,
        { blacklistField },
        auth0
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', integrationKey] });
      queryClient.invalidateQueries({ queryKey: ['integrationFields', integrationKey] });
    },
  });

  return {
    fields: fieldsQuery.data?.fields || [],
    blacklistedFields: fieldsQuery.data?.blacklistedFields || [],
    isLoading: fieldsQuery.isLoading,
    isError: fieldsQuery.isError,
    error: fieldsQuery.error,
    updateField: updateFieldMutation.mutate,
    updateFieldAsync: updateFieldMutation.mutateAsync,
    isUpdating: updateFieldMutation.isPending,
    updateError: updateFieldMutation.error,
    addOrRemoveBlacklistedField: blacklistFieldMutation.mutate,
    addOrRemoveBlacklistedFieldAsync: blacklistFieldMutation.mutateAsync,
    isBlacklisting: blacklistFieldMutation.isPending,
    blacklistError: blacklistFieldMutation.error,
  };
}; 